// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { QuickTree } from '../QuickTree/QuickTree';
import { QuickTableCell } from './QuickTableCell';
import { QuickTableUtils } from './QuickTableUtils';
import { Rectangle } from '@shared/commons/Geometry';
import { InstrumentLookupQuickTreeNode } from '../QuickTree/InstrumentLookupQuickTreeNode';
import { ThemeManager } from '../../misc/ThemeManager';
import { SolidBrush } from '@shared/commons/Graphics';
import { SymbolLookupViewItemHelper } from '@shared/utils/Instruments/SymbolLookupViewItemHelper';
export class InstrumentLookupAutoCompleteQuickTableCell extends QuickTableCell {
    constructor(rowOwner) {
        super();
        this.InstrumentNameText = '';
        this.InstrumentDescriptionText = '';
        this.InstrumentTradingExchangeText = '';
        // public CountryId = -1;
        this.FlagImg = null;
        this.RowOwner = rowOwner;
    }
    Draw(context, cellBackColor, cellX, cellY, cellW, cellH, rowSelected, rowHovered) {
        const coll_size = QuickTree.COLLAPSE_ICON_SIZE;
        const needCollapseArrow = this.NeedCollapseArrow();
        const collapsed = this.Collapsed();
        const topOffset = 8;
        const rightMargin = 16;
        const font = InstrumentLookupQuickTreeNode.FontObj;
        context.font = font.toString();
        let curX = cellX + coll_size;
        if (this.RowOwner.Level > 0) {
            curX += this.RowOwner.Level * (InstrumentLookupQuickTreeNode.InstrumentIconWidth + 4); // 4 - gap
        }
        const exchangeText = this.RowOwner.getExchange();
        const gapBetweenExAndFlag = 4;
        let exchangeRect = null;
        if (isValidString(exchangeText)) {
            const exchangeTextWidth = Math.ceil(QuickTableUtils.GetWidth(context, exchangeText));
            const exchangeX = cellX +
                cellW -
                rightMargin -
                exchangeTextWidth -
                gapBetweenExAndFlag -
                InstrumentLookupQuickTreeNode.InsFlagWidth;
            exchangeRect = new Rectangle(exchangeX, cellY + topOffset, exchangeTextWidth, font.getCorrectHeight());
        }
        const backgroundBrush = this.getBackgroundBrush(cellBackColor, rowHovered, rowSelected);
        if (!isNullOrUndefined(backgroundBrush)) {
            context.FillRect(backgroundBrush, cellX, cellY, cellW, cellH);
        }
        if (isValidString(this.instrumentTypeText)) {
            const rect = new Rectangle(curX, cellY + topOffset, InstrumentLookupQuickTreeNode.InstrumentIconWidth, InstrumentLookupQuickTreeNode.InstrumentIconHeight);
            InstrumentLookupQuickTreeNode.DrawInstrumentTypeLogo(context, rect, this.instrumentTypeText, this.instrumentTypeColor);
            curX += rect.Width + 4; // 4 - gap
        }
        const textBrush = this.getForegroundBrush(rowHovered, rowSelected);
        const needHighlight = this.NeedHighlight(); // NeedHighlight -> #0f0f0fff
        const mainText = this.InstrumentNameText;
        const mainTextWidth = Math.ceil(QuickTableUtils.GetWidth(context, mainText));
        const insNameRectWidth = !isNullOrUndefined(exchangeRect)
            ? Math.min(exchangeRect.X - curX - 4, mainTextWidth)
            : mainTextWidth; // 4 - gap
        const insNameRect = new Rectangle(curX, cellY + topOffset, insNameRectWidth, font.getCorrectHeight()); // 4 - gap
        context.DrawStringInRectNew(mainText, textBrush, insNameRect, true);
        if (needHighlight) {
            context.DrawMatchStringInRect(mainText, insNameRect, this.textPattern, new SolidBrush(ThemeManager.colors.instrumentLookupAutoCompleteFilterForeground), backgroundBrush, true);
        }
        if (needCollapseArrow) {
            const collapseIconX = insNameRect.Right() + 8; // 8 - gap
            const rec = new Rectangle(collapseIconX, cellY + topOffset, coll_size, coll_size);
            this.DrawCollapseIcon(context, rowHovered, collapsed, rowSelected, rec);
        }
        // Description
        const descrText = this.RowOwner.getDescription();
        if (isValidString(descrText)) {
            const descrX = insNameRect.X;
            const descrBrush = new SolidBrush(rowSelected
                ? ThemeManager.colors.quickTreeNodeForegroundSelected
                : ThemeManager.colors.quickTreeNodeForegroundDescription);
            const descrFont = InstrumentLookupQuickTreeNode.DescriptionFontOgj;
            const descrRect = new Rectangle(descrX, insNameRect.Bottom(), cellW, descrFont.getCorrectHeight());
            context.font = descrFont.toString();
            context.DrawStringInRectNew(descrText, descrBrush, descrRect, true);
            if (needHighlight) {
                context.DrawMatchStringInRect(descrText, descrRect, this.textPattern, new SolidBrush(ThemeManager.colors.instrumentLookupAutoCompleteFilterForeground), backgroundBrush, true);
            }
        }
        // Exchange
        if (isValidString(exchangeText)) {
            const exchangeBrush = new SolidBrush(rowSelected
                ? ThemeManager.colors.quickTreeNodeForegroundSelected
                : ThemeManager.colors.quickTreeNodeForegroundTradingExchange);
            QuickTableUtils.DrawTextInRectWithFont(context, exchangeText, font, exchangeBrush, exchangeRect.X, exchangeRect.Y, exchangeRect.Width, exchangeRect.Height);
            const flagIMG = this.FlagImg;
            if (!isNullOrUndefined(flagIMG)) {
                const flagRect = new Rectangle(exchangeRect.Right() + gapBetweenExAndFlag, exchangeRect.Y, InstrumentLookupQuickTreeNode.InsFlagWidth, InstrumentLookupQuickTreeNode.InsFlagHeight);
                InstrumentLookupQuickTreeNode.DrawExchangeFlag(context, flagRect, flagIMG);
            }
        }
    }
    NeedHighlight() {
        // #113316
        return !isNullOrUndefined(this.RowOwner) ? !this.RowOwner.HasAnyChild : true;
    }
    IsPartOfTree() {
        return !isNullOrUndefined(this.RowOwner) ? this.RowOwner.IsPartOfTree : false;
    }
    NeedCollapseArrow() {
        return !isNullOrUndefined(this.RowOwner) ? this.RowOwner.mayContainTrees && this.RowOwner.HasAnyChild : false;
    }
    MayContainTrees() {
        return !isNullOrUndefined(this.RowOwner) ? this.RowOwner.mayContainTrees : false;
    }
    Collapsed() {
        return !isNullOrUndefined(this.RowOwner) ? this.RowOwner.Collapsed : false;
    }
    setInstrumentTypeDisplay(instrumentType) {
        this.instrumentTypeText = SymbolLookupViewItemHelper.getInstrumentTypeShort(instrumentType);
        this.instrumentTypeColor = SymbolLookupViewItemHelper.getInstrumentTypeColor(instrumentType);
    }
    getBackgroundBrush(parentBackColor, hovered, selected) {
        const colors = ThemeManager.colors;
        let color = null;
        if (selected && hovered) {
            color = colors.selectedAndHoveredRowBackColor;
        }
        else if (selected) {
            color = colors.autocompleteQuickTreeNodeBackgroundSelected;
        }
        else if (hovered) {
            color = colors.quickTreeNodeBackgroundHovered;
        }
        else {
            color = parentBackColor;
        }
        if (isValidString(color)) {
            return new SolidBrush(color);
        }
        else {
            return null;
        }
    }
    DrawCollapseIcon(ctx, hovered, collapsed, selected, rect) {
        if (isNullOrUndefined(ctx) || isNullOrUndefined(rect)) {
            return;
        }
        let img;
        if (collapsed) {
            if (selected) {
                img = ThemeManager.NewImages.quickTreeExpandSelected.image;
            }
            else if (hovered) {
                img = ThemeManager.NewImages.quickTreeExpandHovered.image;
            }
            else {
                img = ThemeManager.NewImages.quickTreeExpandDefault.image;
            }
        }
        else {
            if (selected) {
                img = ThemeManager.NewImages.autocompleteQuickTreeCollapseSelected.image;
            }
            else if (hovered) {
                img = ThemeManager.NewImages.autocompleteQuickTreeCollapseHovered.image;
            }
            else {
                img = ThemeManager.NewImages.autocompleteQuickTreeCollapseDefault.image;
            }
        }
        ctx.DrawCenteredImage(img, rect, img.width, img.height);
    }
    getForegroundBrush(isHovered, isSelected) {
        let color = '';
        if (isSelected) {
            color = ThemeManager.colors.quickTreeNodeForegroundSelected;
        }
        else if (isHovered) {
            color = ThemeManager.colors.quickTreeNodeForegroundHovered;
        }
        else {
            color = ThemeManager.colors.quickTreeNodeForeground;
        }
        return new SolidBrush(color);
    }
}
